import { AssetVizController } from '@asset-viz/defines'
import { layerBoundingRect } from 'features/libra/workspacePanels/ffsLayerBounds'
import { applyLayerVisualState } from 'features/libra/workspacePanels/ffsLayerVisualState'
import { RefObject, useCallback, useMemo, useState } from 'react'
import { GeckoMarkupLayer } from 'types'

// Accent used to emphasize a hovered markup on the model. Electric violet is
// distinct from the CTP/pending blue and every other markup color, and stands
// out against the orange/yellow/green heatmap.
const HOVER_HIGHLIGHT_COLOR = '#6A00FF'

interface UseFfsLayerViewStateArgs {
  /** Ambient FFS markup layers (indications, CTPs, folded flaw boundaries). */
  ffsMarkupLayers: ReadonlyArray<GeckoMarkupLayer>
  /** Transient point-and-click coordinate layers. */
  coordinateMarkupLayers: ReadonlyArray<GeckoMarkupLayer>
  /** Ref to the viz controller, for the zoom-to-markup action. */
  vizRef: RefObject<AssetVizController>
}

/** The `FfsLayerVisibilityControl` prop bag driven by this hook. */
interface FfsLayerControlProps {
  hiddenLayerIds: ReadonlySet<string>
  hoveredLayerIds: ReadonlySet<string>
  onSetLayersHidden: (layerIds: string[], hidden: boolean) => void
  onHoverLayer: (layerIds: string[] | null) => void
  onZoomToLayer: (layer: GeckoMarkupLayer) => void
}

interface UseFfsLayerViewStateResult {
  /** The markup layers to hand to the viewer (hidden dropped, hovered recolored). */
  markupLayers: GeckoMarkupLayer[]
  /** Props to spread onto `FfsLayerVisibilityControl`. */
  layerControlProps: FfsLayerControlProps
}

/**
 * Owns the panel-local layer view-state (which layers are hidden / hovered) and
 * derives both the viewer's markup-layer array and the layer-visibility control's
 * props. Kept out of the workspace context so hover churn stays local to the viz
 * panel + its overlay control.
 */
export function useFfsLayerViewState({
  ffsMarkupLayers,
  coordinateMarkupLayers,
  vizRef,
}: UseFfsLayerViewStateArgs): UseFfsLayerViewStateResult {
  const [hiddenLayerIds, setHiddenLayerIds] = useState<ReadonlySet<string>>(
    () => new Set()
  )
  const [hoveredLayerIds, setHoveredLayerIds] = useState<ReadonlySet<string>>(
    () => new Set()
  )

  // Bulk show/hide the given layer ids -- used for a single row (its layerIds)
  // and a whole category. `hidden=true` hides all given ids.
  const onSetLayersHidden = useCallback(
    (layerIds: string[], hidden: boolean) => {
      setHiddenLayerIds((prev) => {
        const next = new Set(prev)
        for (const id of layerIds) {
          if (hidden) {
            next.add(id)
          } else {
            next.delete(id)
          }
        }
        return next
      })
    },
    []
  )

  const onHoverLayer = useCallback((layerIds: string[] | null) => {
    setHoveredLayerIds(layerIds ? new Set(layerIds) : new Set())
  }, [])

  // Frame the camera on a markup layer. Only the single-component DefaultCamera
  // wires zoomToFit; the placeholder/multi-component controller rejects, so
  // swallow it.
  const onZoomToLayer = useCallback(
    (layer: GeckoMarkupLayer) => {
      const rect = layerBoundingRect(layer)
      if (rect) vizRef.current?.zoomToFit(rect).catch(() => {})
    },
    [vizRef]
  )

  // Hidden layers are dropped from the prop array entirely (`Surface3D`
  // re-derives geometry from `markupLayers` each render, so removal unmounts the
  // geometry) and hovered layers are recolored to stand out on the model.
  const markupLayers = useMemo(
    () =>
      applyLayerVisualState([...ffsMarkupLayers, ...coordinateMarkupLayers], {
        hiddenLayerIds,
        hoveredLayerIds,
        hoverColor: HOVER_HIGHLIGHT_COLOR,
      }),
    [ffsMarkupLayers, coordinateMarkupLayers, hiddenLayerIds, hoveredLayerIds]
  )

  const layerControlProps = useMemo<FfsLayerControlProps>(
    () => ({
      hiddenLayerIds,
      hoveredLayerIds,
      onSetLayersHidden,
      onHoverLayer,
      onZoomToLayer,
    }),
    [
      hiddenLayerIds,
      hoveredLayerIds,
      onSetLayersHidden,
      onHoverLayer,
      onZoomToLayer,
    ]
  )

  return { markupLayers, layerControlProps }
}
